import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Send, Mic, MicOff, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { sendMessage } from '../../api/aurora';
import { AppIcon } from '../../components/AppIcon';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}
const SUGGESTED_QUESTIONS = ['What is a normal fetal heart rate?', 'How do I count baby kicks?', 'What vaccines does my baby need?', 'What is AEFI?', 'What are fetal hiccups?', 'Signs of healthy baby movement'];
export function AuroraChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token
  } = useAuth();
  const [conversationId, setConversationId] = useState<string | undefined>(location.state?.conversationId);
  const [messages, setMessages] = useState<Message[]>(location.state?.messages || []);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  // Speech Synthesis
  useEffect(() => {
    const handleEnd = () => setIsSpeaking(false);
    window.speechSynthesis.addEventListener('end', handleEnd);
    return () => {
      window.speechSynthesis.removeEventListener('end', handleEnd);
      window.speechSynthesis.cancel();
    };
  }, []);
  const toggleSpeech = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const lastAuroraMessage = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAuroraMessage) return;
    const utterance = new SpeechSynthesisUtterance(lastAuroraMessage.content);
    const voices = window.speechSynthesis.getVoices();
    const femaleVoice = voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
    const englishVoice = voices.find(v => v.lang.startsWith('en'));
    if (femaleVoice) utterance.voice = femaleVoice;else if (englishVoice) utterance.voice = englishVoice;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };
  const handleSend = async (text: string) => {
    if (!text.trim() || !token || isTyping) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setError(null);
    // Stop speaking if user sends a new message
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
    try {
      const response = await sendMessage(token, text.trim(), conversationId);
      if (!conversationId && response.conversationId) {
        setConversationId(response.conversationId);
      }
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: (typeof response.message === 'object' ? response.message?.content : response.message) || response.content || 'I am here to help.',
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError('Aurora is unavailable right now. Please try again.');
    } finally {
      setIsTyping(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue);
    }
  };
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const processed = lines.map(line => {
      // Headings
      if (line.startsWith('### ')) return `<h4 class="text-[15px] font-semibold text-text-primary mt-3 mb-1">${line.slice(4)}</h4>`;
      if (line.startsWith('## ')) return `<h3 class="text-[16px] font-semibold text-text-primary mt-4 mb-1">${line.slice(3)}</h3>`;
      if (line.startsWith('# ')) return `<h2 class="text-[17px] font-bold text-text-primary mt-4 mb-2">${line.slice(2)}</h2>`;
      // Bullet points
      if (line.match(/^- /)) return `<div class="flex gap-2 ml-1"><span>•</span><span>${line.slice(2)}</span></div>`;
      // Empty lines
      if (line.trim() === '') return '<div class="h-2"></div>';
      // Regular text
      return `<p>${line}</p>`;
    });
    // Apply bold formatting
    const html = processed.join('').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <div className="space-y-0.5" dangerouslySetInnerHTML={{
      __html: html
    }} data-id="element-598" data-mp="38oj4g1f0womw" />;
  };
  return <div className="flex flex-col h-[calc(100vh-64px)] md:h-[calc(100vh-80px)] bg-background" data-id="element-599" data-mp="2yquzyjrm82rj">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10 shadow-sm" data-id="element-600" data-mp="2uormjpldyo3m">
        <div className="flex items-center gap-3" data-id="element-601" data-mp="2prhxib1wsegb">
          <AppIcon size={36} data-id="element-602" data-mp="sxwqcwn9omwd" />
          <div data-id="element-603" data-mp="swvbeh34rg9d">
            <h1 className="text-[16px] font-semibold text-text-primary leading-tight" data-id="element-604" data-mp="swlgqg754kxc">
              Aurora
            </h1>
            <p className="text-[12px] text-text-secondary" data-id="element-605" data-mp="nwqreh30k3ee">
              AI Health Assistant
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/app/aurora/history')} className="p-2 text-text-secondary hover:text-primary hover:bg-primary/5 rounded-full transition-colors" aria-label="Chat History" data-id="element-606" data-mp="1buorkxh0719a">
          <Clock size={22} data-id="element-607" data-mp="132d1xdtprhud" />
        </button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar" data-id="element-608" data-mp="1qsh4dhv61bjs">
        {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center max-w-[400px] mx-auto px-4" data-id="element-609" data-mp="1urpveeytmvbn">
            <AppIcon size={64} className="mb-6 opacity-80" data-id="element-610" data-mp="1gt02zcmy5jtm" />
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-611" data-mp="1lrjlt468ufhr">
              Hi, I'm Aurora
            </h2>
            <p className="text-[15px] text-text-secondary mb-8" data-id="element-612" data-mp="5ci4l6zsh74c">
              I'm your AI health assistant. I can help answer questions about
              your pregnancy, fetal monitoring, and general maternal health.
            </p>
          </div> : <div className="space-y-6 pb-4" data-id="element-613" data-mp="3u0r8bl5gxuj3">
            {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-614" data-mp="bu32tu6ib5cw">
                {msg.role === 'assistant' && <div className="flex-shrink-0 mr-2 mt-1" data-id="element-615" data-mp="3p1ej4tr4kbdc">
                    <AppIcon size={28} data-id="element-616" data-mp="3p2pqf3js4197" />
                  </div>}
                <div className={`max-w-[80%] md:max-w-[70%] flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-617" data-mp="3k8tue6n2sogo">
                  <div className={`px-4 py-3 rounded-[20px] text-[15px] leading-relaxed ${msg.role === 'user' ? 'bg-primary text-white rounded-tr-[4px]' : 'bg-white text-text-primary shadow-sm border border-border/50 rounded-tl-[4px]'}`} data-id="element-618" data-mp="2rkfwdqvzls54">
                    {msg.role === 'assistant' ? renderMarkdown(msg.content) : <span className="whitespace-pre-wrap" data-id="element-619" data-mp="2l0gld3if7m7x">{msg.content}</span>}
                  </div>
                  <span className="text-[11px] text-text-secondary mt-1 px-1" data-id="element-620" data-mp="2py9n9kcoxu2e">
                    {formatTime(msg.createdAt)}
                  </span>
                </div>
              </motion.div>)}

            {isTyping && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex justify-start" data-id="element-621" data-mp="33pn4grn4mfu9">
                <div className="flex-shrink-0 mr-2 mt-1" data-id="element-622" data-mp="120qxbj66pax2">
                  <AppIcon size={28} data-id="element-623" data-mp="122bs99weuvvg" />
                </div>
                <div className="bg-white px-4 py-4 rounded-[20px] rounded-tl-[4px] shadow-sm border border-border/50 flex items-center gap-1" data-id="element-624" data-mp="x1ef5bq9cs0t">
                  <motion.div animate={{
              y: [0, -5, 0]
            }} transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: 0
            }} className="w-2 h-2 bg-primary/40 rounded-full" data-id="element-625" data-mp="s2tcl5nj9w97" />
                  <motion.div animate={{
              y: [0, -5, 0]
            }} transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: 0.2
            }} className="w-2 h-2 bg-primary/60 rounded-full" data-id="element-626" data-mp="1b04c8rd2urp6" />
                  <motion.div animate={{
              y: [0, -5, 0]
            }} transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: 0.4
            }} className="w-2 h-2 bg-primary/80 rounded-full" data-id="element-627" data-mp="2so8u1jxshhn0" />
                </div>
              </motion.div>}

            {error && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex justify-start" data-id="element-628" data-mp="3bla0zaawlrtl">
                <div className="flex-shrink-0 mr-2 mt-1" data-id="element-629" data-mp="3ggfwn0u3ykuv">
                  <AppIcon size={28} data-id="element-630" data-mp="3vcoubxycoq56" />
                </div>
                <div className="max-w-[80%] md:max-w-[70%]" data-id="element-631" data-mp="3qciz67svglic">
                  <div className="px-4 py-3 rounded-[20px] rounded-tl-[4px] bg-white text-[15px] text-text-secondary leading-relaxed shadow-sm border border-border/50" data-id="element-632" data-mp="3qe3tpxf6s4g7">
                    Aurora is having trouble connecting. Please try again
                    shortly.
                  </div>
                </div>
              </motion.div>}
          </div>}
        <div ref={messagesEndRef} data-id="element-633" data-mp="8ssf6hxnat4j" />
      </div>

      {/* Suggested Questions (Only when empty) */}
      {messages.length === 0 && <div className="flex-shrink-0 overflow-x-auto hide-scrollbar px-4 pb-4" data-id="element-634" data-mp="3q88rn31xsi79">
          <div className="flex gap-2 w-max" data-id="element-635" data-mp="3v2mshkzdbhjb">
            {SUGGESTED_QUESTIONS.map((q, i) => <button key={i} onClick={() => handleSend(q)} className="px-4 py-2 bg-white border border-border rounded-pill text-[13px] font-medium text-text-primary hover:border-primary hover:text-primary transition-colors whitespace-nowrap shadow-sm" data-id="element-636" data-mp="3hb86r6e2t28k" data-mpi={i}>
                {q}
              </button>)}
          </div>
        </div>}

      {/* Input Area */}
      <div className="flex-shrink-0 bg-white border-t border-border p-3 md:p-4 pb-[calc(12px+env(safe-area-inset-bottom))]" data-id="element-637" data-mp="2of8oo8eecb3s">
        <div className="max-w-[800px] mx-auto relative flex items-end gap-2" data-id="element-638" data-mp="2jfm8pr76ogra">
          <div className="flex-1 bg-background border border-border rounded-[20px] flex items-end focus-within:border-primary focus-within:shadow-focus transition-all overflow-hidden" data-id="element-639" data-mp="2eh2lzokrn0xv">
            <textarea ref={inputRef} value={inputValue} onChange={e => {
            if (e.target.value.length <= 500) {
              setInputValue(e.target.value);
            }
          }} onKeyDown={handleKeyDown} placeholder="Ask Aurora anything..." className="w-full max-h-[120px] min-h-[44px] bg-transparent border-none resize-none py-3 px-4 text-[15px] text-text-primary placeholder:text-text-secondary focus:outline-none custom-scrollbar" rows={1} style={{
            height: 'auto'
          }} onInput={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
          }} data-id="element-640" data-mp="3c2gtkp7wxi5a" />

            {messages.length > 0 && <button onClick={toggleSpeech} className={`p-3 flex-shrink-0 transition-colors ${isSpeaking ? 'text-accent' : 'text-text-secondary hover:text-primary'}`} aria-label={isSpeaking ? 'Stop speaking' : 'Read last message'} data-id="element-641" data-mp="kz3nxddpj183">
                {isSpeaking ? <motion.div animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} data-id="element-642" data-mp="yxlb99zpabph">
                    <Mic size={20} data-id="element-643" data-mp="v04mfpdbth0z" />
                  </motion.div> : <MicOff size={20} data-id="element-644" data-mp="q2w8k0z7nz5j" />}
              </button>}
          </div>

          <button onClick={() => handleSend(inputValue)} disabled={!inputValue.trim() || isTyping} className="w-[44px] h-[44px] flex-shrink-0 bg-primary text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:bg-border disabled:text-text-secondary transition-colors" aria-label="Send message" data-id="element-645" data-mp="140ujewebpdga">
            <Send size={20} className="ml-0.5" data-id="element-646" data-mp="l39s7io362si" />
          </button>
        </div>

        {inputValue.length > 400 && <div className={`text-right text-[11px] mt-1 pr-14 ${inputValue.length >= 500 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-647" data-mp="gbi033pa7p09">
            {inputValue.length} / 500
          </div>}
      </div>

      <style data-id="element-648" data-mp="3kof5se0be8nx">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>;
}